import { type CommunityAssociation } from "@entities";
import { Box, Typography } from "@mui/material";
import { GENDER_TO_HEB } from "@shared/helpers/constants";
import { type ColumnProps } from "@shared/frontend/hyper-table/types";
import dayjs from "dayjs";
import PhoneNumberCell from "../tables/PhoneNumberCell";
import type { CommunityMemberActivity } from "./communityDashboardApi";

const ACTIVITY_STATUS_LABELS: Record<string, string> = {
  NEW: "חדש",
  ACTIVE: "פעיל",
  INACTIVE: "לא פעיל",
  LEGACY: "מאגר ישן",
};

const ACTIVITY_STATUS_COLORS: Record<string, string> = {
  NEW: "#F9B035",
  ACTIVE: "#06D6A0",
  INACTIVE: "#EF476F",
  LEGACY: "#5B8DEF",
};

function formatAge(dateOfBirth: Date | string | null | undefined) {
  if (!dateOfBirth) return "-";
  const dob = dayjs(dateOfBirth);
  if (!dob.isValid()) return "-";
  const age = dayjs().diff(dob, "year");
  if (age < 0 || age > 120) return "-";
  return String(age);
}

function formatDate(value: Date | string | null | undefined) {
  if (!value) return "-";
  const date = dayjs(value);
  if (!date.isValid()) return "-";
  return date.format("DD/MM/YYYY");
}

function formatHours(value: number | undefined) {
  if (value == null) return "…";
  return value.toLocaleString("he-IL", { maximumFractionDigits: 2 });
}

export function buildCommunityMemberColumns(): ColumnProps<CommunityAssociation>[] {
  return [
    {
      field: "association.user.name",
      caption: "שם",
      minSize: 160,
      cell: ({ row }) => row.original.association?.user?.name || "-",
    },
    {
      field: "association.user.phoneNumber",
      caption: "טלפון",
      minSize: 150,
      preventRowClick: true,
      cell: ({ row }) => (
        <PhoneNumberCell
          phoneNumber={row.original.association?.user?.phoneNumber || ""}
        />
      ),
    },
    {
      field: "association.user.email",
      caption: "אימייל",
      minSize: 160,
      cell: ({ row }) => row.original.association?.user?.email?.trim() || "-",
    },
    {
      field: "association.user.gender",
      caption: "מגדר",
      minSize: 80,
      cell: ({ row }) => {
        const gender = row.original.association?.user?.gender;
        if (!gender) return "-";
        return GENDER_TO_HEB[gender] || gender;
      },
    },
    {
      field: "association.user.dateOfBirth",
      caption: "גיל",
      minSize: 70,
      cell: ({ row }) => formatAge(row.original.association?.user?.dateOfBirth),
    },
    {
      field: "createdAt",
      caption: "הצטרפות לקהילה",
      minSize: 140,
      cell: ({ row }) => formatDate(row.original.createdAt),
    },
    {
      field: "association.activityStatus",
      caption: "סטטוס פעילות",
      minSize: 130,
      cell: ({ row }) => {
        const status = row.original.association?.activityStatus ?? "";
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                bgcolor: ACTIVITY_STATUS_COLORS[status] ?? "text.disabled",
              }}
            />
            <Typography variant="body2">
              {ACTIVITY_STATUS_LABELS[status] ?? "-"}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "isLeader",
      caption: "מוביל קהילה",
      enableSorting: false,
      minSize: 110,
      cell: ({ row }) => (row.original.isLeader ? "כן" : "לא"),
    },
  ];
}

export function communityMemberActivityCells(
  activityByAssociationId: Record<string, CommunityMemberActivity | undefined>,
) {
  return {
    participations: (row: CommunityAssociation) => {
      const activity = activityByAssociationId[row.associationId];
      if (!activity) return "…";
      return activity.approvedParticipationsCount.toLocaleString("he-IL");
    },
    lastAt: (row: CommunityAssociation) => {
      const activity = activityByAssociationId[row.associationId];
      if (!activity) return "…";
      return formatDate(activity.lastParticipationAt);
    },
    hours: (row: CommunityAssociation) =>
      formatHours(activityByAssociationId[row.associationId]?.reportedHours),
  };
}
